import { useState } from 'react'
import { useAuthStore } from '../store/authStore'
import { useTranslation } from 'react-i18next'
import { Building2 } from 'lucide-react'
import NewPasswordForm from './NewPasswordForm'
import PasswordInput from './components/PasswordInput'

const Login = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const { login, pendingNewPassword, completeNewPassword, cancelNewPassword } = useAuthStore()
  const { t } = useTranslation(['common', 'pages', 'errors', 'forms', 'badges', 'navigation'])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      await login(email, password)
    } catch (err) {
      console.error('[Login] Authentication failed:', err)
      setError(t('errors:invalidCredentials'))
    } finally {
      setLoading(false)
    }
  }

  const handleCompleteNewPassword = async (newPassword: string): Promise<void> => {
    try {
      await completeNewPassword(newPassword)
    } catch (err) {
      const message = err instanceof Error ? err.message : ''
      if (message === 'Your session has expired. Please sign in again.') {
        // Store already reset pendingNewPassword — credential form will reappear.
        // Carry the error forward so it shows in the credential form's error slot.
        setError(t('errors:challengeSessionExpired'))
        return
      }
      // Policy violations and service errors are handled inside NewPasswordForm itself.
      throw err
    }
  }

  if (pendingNewPassword) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 via-white to-accent-50">
        <div className="bg-white p-8 rounded-2xl shadow-xl w-full max-w-md border border-gray-100">
          <div className="flex items-center justify-center mb-8">
            <div className="bg-gradient-to-br from-primary-500 to-primary-600 p-3 rounded-xl shadow-lg">
              <Building2 size={32} className="text-white" />
            </div>
          </div>
          <NewPasswordForm onSubmit={handleCompleteNewPassword} onCancel={cancelNewPassword} />
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 via-white to-accent-50">
      <div className="bg-white p-8 rounded-2xl shadow-xl w-full max-w-md border border-gray-100">
        <div className="flex items-center justify-center mb-8">
          <div className="bg-gradient-to-br from-primary-500 to-primary-600 p-3 rounded-xl shadow-lg">
            <Building2 size={32} className="text-white" />
          </div>
        </div>

        <h1 className="text-3xl font-bold text-center mb-2 text-gray-900">{t('pages:loginHeading')}</h1>
        <p className="text-secondary-500 text-center mb-8">{t('pages:loginSubHeading')}</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm border border-red-200">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-1">
              {t('forms:emailAddress')}
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
              placeholder="admin@example.com"
              required
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-1">
              {t('forms:password')}
            </label>
            <PasswordInput
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input"
              placeholder="••••••••"
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed mt-6"
          >
            {loading ? t('pages:loginButtonLoading') : t('pages:loginButton')}
          </button>
        </form>

        <p className="text-xs text-gray-500 text-center mt-6">
          {t('pages:loginFooter')}
        </p>
      </div>
    </div>
  )
}

export default Login
